"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  indexParts,
  isPsatSection,
  isSelectionIndex,
  mathNeeded,
  readingWritingNeeded,
  selectionIndex,
} from "@/lib/tool-kit/exam-scores/psat";

/** English strings. A translated content file overrides these through `ui`. */
export const PSAT_SELECTION_INDEX_CALCULATOR_STRINGS = {
  inputHeading: "Your PSAT/NMSQT section scores",
  inputHint:
    "Each section is scored from 160 to 760 in steps of 10. The index runs from 48 to 228.",
  rwLabel: "Reading and Writing (160–760)",
  mathLabel: "Math (160–760)",
  invalid: "{section} must be a score from 160 to 760 in steps of 10.",
  sections: {
    readingWriting: "Reading and Writing",
    math: "Math",
  },
  results: "Selection Index",
  index: "NMSC Selection Index",
  total: "Total score",
  fromRw: "Points from Reading and Writing (doubled)",
  fromMath: "Points from Math",
  working: "(2 × {rw} + {math}) ÷ 10 = {index}",
  enter: "Enter both section scores to see the Selection Index.",
  targetHeading: "Work backward from an index",
  targetLabel: "Selection Index you want (48–228)",
  targetInvalid: "Enter a whole number from 48 to 228.",
  needMath:
    "With Reading and Writing at {rw}, the lowest Math score that reaches {target} is {score}.",
  needMathNone:
    "With Reading and Writing at {rw}, no Math score reaches {target}.",
  needRw:
    "With Math at {math}, the lowest Reading and Writing score that reaches {target} is {score}.",
  needRwNone:
    "With Math at {math}, no Reading and Writing score reaches {target}.",
  needHint: "Enter a valid section score above to work out the other one.",
  gridHeading: "Selection Index by section scores",
  gridCorner: "Reading and Writing ↓ · Math →",
  cutoffsNote:
    "National Merit state cutoffs are set by NMSC each year and differ by state and year. This page does not list or predict them: see the National Merit Scholarship Corporation's website for the published cutoffs.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "PSAT/NMSQT Selection Index {index} (Reading and Writing {rw}, Math {math})",
} as const;

type Strings = typeof PSAT_SELECTION_INDEX_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const SCORE = { useGrouping: false } as const;
const GRID = [760, 720, 680, 640, 600, 560, 520] as const;

function read(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

function TargetPanel({
  rw,
  math,
  strings,
  fmt,
}: {
  rw: number | null;
  math: number | null;
  strings: Strings;
  fmt: Format;
}) {
  const [raw, setRaw] = useState("");
  const target = read(raw);
  const valid = isSelectionIndex(target);
  const lines: string[] = [];
  if (valid && isPsatSection(rw)) {
    const needed = mathNeeded(rw, target);
    lines.push(
      needed === null
        ? t(strings.needMathNone, { rw: fmt(rw, SCORE), target: fmt(target) })
        : t(strings.needMath, {
            rw: fmt(rw, SCORE),
            target: fmt(target),
            score: fmt(needed, SCORE),
          })
    );
  }
  if (valid && isPsatSection(math)) {
    const needed = readingWritingNeeded(math, target);
    lines.push(
      needed === null
        ? t(strings.needRwNone, { math: fmt(math, SCORE), target: fmt(target) })
        : t(strings.needRw, {
            math: fmt(math, SCORE),
            target: fmt(target),
            score: fmt(needed, SCORE),
          })
    );
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.targetHeading}</h3>
      <TextField
        id="psat-target"
        label={strings.targetLabel}
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div aria-live="polite" className="space-y-1 text-foreground text-sm">
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {valid && lines.length === 0 ? (
          <Notice>{strings.needHint}</Notice>
        ) : null}
        {valid || raw.trim() === "" ? null : (
          <Notice>{strings.targetInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

export default function PsatSelectionIndexCalculatorWidget() {
  const strings = useToolUi(PSAT_SELECTION_INDEX_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [rwRaw, setRwRaw] = useState("700");
  const [mathRaw, setMathRaw] = useState("690");
  const rw = read(rwRaw);
  const math = read(mathRaw);
  const ready = isPsatSection(rw) && isPsatSection(math);
  const parts = ready ? indexParts(rw, math) : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputHint}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="psat-rw"
          label={strings.rwLabel}
          numeric
          onChange={setRwRaw}
          value={rwRaw}
        />
        <TextField
          id="psat-math"
          label={strings.mathLabel}
          numeric
          onChange={setMathRaw}
          value={mathRaw}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {rwRaw.trim() !== "" && !isPsatSection(rw) ? (
          <Notice>
            {t(strings.invalid, { section: strings.sections.readingWriting })}
          </Notice>
        ) : null}
        {mathRaw.trim() !== "" && !isPsatSection(math) ? (
          <Notice>
            {t(strings.invalid, { section: strings.sections.math })}
          </Notice>
        ) : null}
        {parts && ready ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.index}
                value={fmt(parts.index)}
              />
              <StatCard label={strings.total} value={fmt(rw + math, SCORE)} />
              <StatCard
                label={strings.fromRw}
                value={fmt(parts.fromReadingWriting)}
              />
              <StatCard label={strings.fromMath} value={fmt(parts.fromMath)} />
            </StatGrid>
            <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
              {t(strings.working, {
                rw: fmt(rw, SCORE),
                math: fmt(math, SCORE),
                index: fmt(parts.index),
              })}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                index: fmt(parts.index),
                rw: fmt(rw, SCORE),
                math: fmt(math, SCORE),
              })}
            />
          </>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
        <p className="text-muted-foreground text-sm">{strings.cutoffsNote}</p>
      </section>

      <TargetPanel fmt={fmt} math={math} rw={rw} strings={strings} />

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.gridHeading}</h3>
        <DataTable
          head={[strings.gridCorner, ...GRID.map((score) => fmt(score, SCORE))]}
          rows={GRID.map((rwScore) => [
            fmt(rwScore, SCORE),
            ...GRID.map((mathScore) => fmt(selectionIndex(rwScore, mathScore))),
          ])}
        />
      </section>
    </div>
  );
}
